import React, { useEffect, useMemo, useState } from 'react'
import * as UIcons from '@iconscout/react-unicons'
import { toast } from 'react-toastify'
import { useDispatch } from 'react-redux'
import useSWR from 'swr'
import dayjs from 'dayjs'
import { withRouter } from 'hooks/withRouter'
import BreadCrumb from 'components/BreadCrumb'
import { setNavPageContext, clearNavPageContext } from 'store/slices/miscSlice'
import { getMockCampaignDetail } from './mock/campaignDetail'
import CampaignDetailHeader from './CampaignDetailHeader'
import OverviewTab from './tabs/OverviewTab'
import AnalyticsTab from './tabs/AnalyticsTab'
import TransactionsTab from './tabs/TransactionsTab'
import ParticipantsTab from './tabs/ParticipantsTab'
import QuizTab from './tabs/QuizTab'
import PredictionTab from './tabs/PredictionTab'
import StreakTab from './tabs/StreakTab'
import BranchTab from './tabs/BranchTab'
import CustomerInsightTab from './tabs/CustomerInsightTab'
import PrizesTab from './tabs/PrizesTab'
import ReviewTab from './tabs/ReviewTab'

const TABS = [
  { key: 'overview', label: 'Overview', icon: UIcons.UilApps, Component: OverviewTab },
  { key: 'analytics', label: 'Analytics', icon: UIcons.UilChartLine, Component: AnalyticsTab },
  { key: 'transactions', label: 'Transactions', icon: UIcons.UilExchange, Component: TransactionsTab },
  { key: 'participants', label: 'Participants', icon: UIcons.UilUsersAlt, Component: ParticipantsTab },
  { key: 'quiz', label: 'Quiz', icon: UIcons.UilQuestionCircle, Component: QuizTab },
  { key: 'prediction', label: 'Prediction', icon: UIcons.UilChartBar, Component: PredictionTab },
  { key: 'streak', label: 'Streak', icon: UIcons.UilSync, Component: StreakTab },
  { key: 'branch', label: 'Branch', icon: UIcons.UilMapMarker, Component: BranchTab },
  { key: 'customerInsight', label: 'Customer Insight', icon: UIcons.UilUserCheck, Component: CustomerInsightTab },
  { key: 'prizes', label: 'Prizes', icon: UIcons.UilGift, Component: PrizesTab },
  { key: 'review', label: 'Review', icon: UIcons.UilStar, Component: ReviewTab }
]

// promos/campaigns/{id} only carries title/description/status/budget/dates — the
// rest of this page (participants, quiz/prediction/streak/branch mechanics, prizes,
// reviews) has no backing endpoint yet, so it stays on the mock scaffold (matching
// the RewardAds detail page's "not yet wired to a backend" convention).
function buildCampaignDetail(id, real) {
  const mock = getMockCampaignDetail(id)
  if (!real) return mock

  const daysLeft = real.end ? Math.max(0, dayjs(real.end).diff(dayjs(), 'day')) : mock.period.daysLeft
  const periodLabel = real.start && real.end
    ? `${dayjs(real.start).format('MMM D')} - ${dayjs(real.end).format('MMM D, YYYY')}`
    : mock.period.label

  return {
    ...mock,
    id: real.id,
    title: real.title ?? mock.title,
    description: real.description ?? mock.description,
    status: (real.status ?? mock.status).toLowerCase(),
    budget: real.budget ?? mock.budget,
    thumbnailUrl: real.thumbnail_stream,
    period: { ...mock.period, label: periodLabel, daysLeft }
  }
}

export default withRouter(function CampaignDetail({ router: { params, navigate } }) {
  const { data, isValidating } = useSWR(`promos/campaigns/${params.id}`)
  const real = data?.data

  const campaign = useMemo(() => buildCampaignDetail(params.id, real), [params.id, real])
  const [activeTab, setActiveTab] = useState('overview')
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(setNavPageContext({ eyebrow: 'Active Campaign', title: campaign.title }))
    return () => dispatch(clearNavPageContext())
  }, [dispatch, campaign.title])

  const ActiveTabComponent = TABS.find((tab) => tab.key === activeTab)?.Component ?? OverviewTab
  const notReady = () => toast.info('This action is coming soon')

  if (isValidating && !data) {
    return (
      <section className="w-full max-w-6xl mx-auto pb-10">
        <p className="text-sm text-secondary py-10 text-center">Loading campaign...</p>
      </section>
    )
  }

  return (
    <section className="w-full max-w-6xl mx-auto pb-10">
      <BreadCrumb
        items={[
          { name: 'Campaigns', onClick: () => navigate('/promo') },
          { name: campaign.title }
        ]}
      />

      <CampaignDetailHeader
        campaign={campaign}
        onEdit={notReady}
        onDuplicate={notReady}
        onEndCampaign={notReady}
      />

      <div className="flex items-center gap-1 border-b border-border-lines mb-4 overflow-x-auto">
        {
          TABS.map(({ key, label, icon: Icon }) => {
            const isActive = key === activeTab
            return (
              <button
                key={key}
                type="button"
                onClick={() => setActiveTab(key)}
                className={`flex items-center gap-1.5 px-3.5 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px ${
                  isActive ? 'border-primary text-primary' : 'border-transparent text-secondary hover:text-dark-blue'
                }`}
              >
                <Icon size={16} /> {label}
              </button>
            )
          })
        }
      </div>

      <ActiveTabComponent campaign={campaign} />
    </section>
  )
})
